import type { CanvasItem, CCCBlindnessCycle, DrawingServerEvent } from "../../shared/types";

// Drawing events can fire many times per second while the artist is drawing, so
// they're kept out of the main store (which would otherwise re-render every
// subscriber on each stroke point). Only the canvas view listens here.
type EventListener = (event: DrawingServerEvent) => void;
type SyncListener = (items: CanvasItem[]) => void;
type BlindnessListener = (cycle: CCCBlindnessCycle | null) => void;

class CanvasBus {
  private eventListeners = new Set<EventListener>();
  private syncListeners = new Set<SyncListener>();
  private blindnessListeners = new Set<BlindnessListener>();
  private lastSync: CanvasItem[] = [];

  onEvent(listener: EventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  onSync(listener: SyncListener): () => void {
    this.syncListeners.add(listener);
    return () => this.syncListeners.delete(listener);
  }

  onBlindness(listener: BlindnessListener): () => void {
    this.blindnessListeners.add(listener);
    return () => this.blindnessListeners.delete(listener);
  }

  emitEvent(event: DrawingServerEvent): void {
    for (const listener of this.eventListeners) listener(event);
  }

  emitSync(items: CanvasItem[]): void {
    this.lastSync = items;
    for (const listener of this.syncListeners) listener(items);
  }

  emitBlindness(cycle: CCCBlindnessCycle | null): void {
    for (const listener of this.blindnessListeners) listener(cycle);
  }

  getLastSync(): CanvasItem[] {
    return this.lastSync;
  }
}

export const canvasBus = new CanvasBus();
